import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import dataOffer from './offers.js';
import assetsPath from '../../constant.js';

/**
 * The Footer-Generic molecule contains links to others pages.
 *
 * didomiUrl - The Didomi script to launch Cookies modal. If didomiUrl is not set, its link is not shown.
 * legalsUrl - The "Legal information" link. If legalsUrl is not set, its link is not shown.
 * termsUrl - The "Terms and conditions" link. If termsUrl is not set, its link is not shown.
 * servicesUrl - The "State of services" link. If servicesUrl is not set, its link is not shown.
 * language - The translation language (ie : fr-FR|en-EN|be-FR|be-EN|fr-BE|en-BE) - Default value : fr-FR.
 */
@customElement('nn-footer-generic')
export class FooterGeneric extends LitElement {
  static styles = css`
    :host {
      background: var(--nn-footer-generic_host_background, #fafbfc);
      border: 0;
      border-radius: 0;
      box-sizing: border-box;
      display: block;
      font-family: font-light;
      font-size: var(--nn-footer-generic_host_font-size, 16px);
      line-height: calc(4px + 2ex);
      margin: 0 auto;
      padding: 0;
      width: 100%;
    }
    #footer-generic,
    #footer-generic * {
      box-sizing: border-box;
    }
    #footer-generic ul,
    #footer-generic li {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
    #footer-generic .container {
      align-items: center;
      display: flex;
      height: auto;
      justify-content: space-evenly;
      margin: 0 auto;
      max-width: 1470px;
      min-height: var(--nn-footer-generic_footer-generic_min-height, 80px);
      padding: 15px;
      width: 100%;
    }
    #footer-generic {
      background: var(--nn-footer-generic_footer-generic_background, #fff);
      border-top: var(--nn-footer-generic_footer-generic_border-top-width, 1px)
        solid var(--nn-footer-generic_footer-generic_border-top-color, #e9e9e9);
      font-family: 'font-light';
      font-size: var(--nn-footer-generic_footer-generic_font-size, 16px);
      margin: 0;
    }
    #footer-generic .category {
      align-items: center;
      display: flex;
      gap: var(--nn-footer-generic_footer-generic_category_gap, 16px);
      justify-content: center;
      padding: var(--nn-footer-generic_category_padding, 0);
      width: 100%;
    }
    #footer-generic .category-item {
      text-align: center;
      position: relative;
    }
    #footer-generic .category-item:before {
      color: var(--nn-footer-generic_category-item--before_color, #fafbfc);
      content: '|';
      display: inline-block;
      font-size: var(
        --nn-footer-generic_category-item--before_font-size,
        0.8125em
      );
      position: absolute;
      left: -10px;
      top: calc(50% - 10px);
    }
    #footer-generic .category-item:first-child:before {
      display: none;
    }
    #footer-generic .category .anchor,
    #footer-generic .category .anchor:active,
    #footer-generic .category .anchor:focus,
    #footer-generic .category .anchor:visited,
    #footer-generic .category .anchor:hover {
      color: var(--nn-footer-generic_category_anchor_color, #323232);
      font-size: var(--nn-footer-generic_anchor_font-size, 0.8125em);
      padding: var(--nn-footer-generic_anchor_padding, 8px 0);
      text-align: center;
      text-decoration: none;
      transition: var(--nn-footer-generic_anchor_transition, 0.3s);
    }
    #footer-generic .category .anchor:hover {
      text-decoration: underline;
    }
    @media (max-width: 900px) {
      :host {
        --nn-footer-generic_anchor_font-size: 0.875em;
      }
      #footer-generic .category {
        align-items: center;
        flex-direction: column;
        justify-content: center;
      }
      #footer-generic .category-item {
        margin: 4px 0;
      }
      #footer-generic .category-item:before {
        display: none;
      }
    }
    @media (max-width: 600px) {
      #footer-generic .container {
        align-items: center;
        flex-direction: column;
        justify-content: center;
        gap: 24px;
      }
      #footer-generic #nordnet-logo {
        order: 2;
      }
      #footer-generic #orange-logo {
        order: 3;
      }
      #footer-generic .category {
        order: 1;
      }
    }
  `;

  @property({ type: String }) language:
    | 'fr-FR'
    | 'en-EN'
    | 'be-FR'
    | 'be-EN'
    | 'fr-BE'
    | 'en-BE' = 'fr-FR';

  @property({ type: String }) didomiUrl = '';
  // i.e. : javascript:Didomi.preferences.show()

  @property({ type: String }) termsUrl = '';

  @property({ type: String }) legalsUrl = '';

  @property({ type: String }) servicesUrl = '';

  renderLink(id: string, text: string, linkTitle: string) {
    if (this.didomiUrl && id === 'manage-cookies') {
      return `<li class="category-item">
        <a
            id="footer-${id}"
            class="anchor"
            href="${this.didomiUrl}"
            title="${linkTitle}"
            target="_self"
            rel="noopener"
        >
          ${text}
        </a>
      </li>`;
    }
    if (this.termsUrl && id === 'rc-cc') {
      return `<li class="category-item">
          <a
              id="footer-${id}"
              class="anchor"
              href="${this.termsUrl}"
              title="${linkTitle}"
              target="_blank"
              rel="nofollow"
          >
            ${text}
          </a>
        </li>`;
    }
    if (this.legalsUrl && id === 'legal-information') {
      return `<li class="category-item">
            <a
                id="footer-${id}"
                class="anchor"
                href="${this.legalsUrl}"
                title="${linkTitle}"
                target="_blank"
                rel="nofollow"
            >
              ${text}
            </a>
          </li>`;
    }
    if (this.servicesUrl && id === 'state-of-services') {
      return `<li class="category-item">
              <a
                  id="footer-${id}"
                  class="anchor"
                  href="${this.servicesUrl}"
                  title="${linkTitle}"
                  target="_blank"
                  rel="nofollow"
              >
                ${text}
              </a>
            </li>`;
    }
    return ``;
  }

  convertDataToHtml(offerId: string) {
    let contentInString: string = '';
    const targetOffer = (dataOffer[this.language] as any[]).find(
      offer => offer.offer === offerId
    );

    if (offerId === 'links') {
      targetOffer.children.forEach((offerChild: any) => {
        contentInString += `${this.renderLink(
          offerChild.id,
          offerChild.text,
          offerChild.linkTitle
        )}`;
      });
    }
    return contentInString;
  }

  render() {
    return html`
      <footer
        id="footer-generic"
        class="footer-generic language-${this.language}"
      >
        <div class="container">
          <img
            id="nordnet-logo"
            src="${assetsPath}/images/logos/logo-nordnet-black.svg"
            alt="Nordnet"
            width="155"
            height="28"
          />
          <ul
            class="category"
            .innerHTML="${this.convertDataToHtml('links')}"
          ></ul>
          <img
            id="orange-logo"
            src="${assetsPath}/images/logos/logo-orange-with-baseline.png"
            alt="Orange"
            width="134"
            height="36"
          />
        </div>
      </footer>
    `;
  }
}
